/**
 * Retelling 分析服务
 * 调用 AI 分析用户复述的关键点覆盖
 */

import { RetellingAnalysis, SubtitleSegment } from '../../../db/shadowDb';
import { callGeminiAudio, callGeminiText } from '../../../session/speaking/genaiClient';

/**
 * 构建 Retelling 分析的 Prompt
 */
function buildRetellingPrompt(subtitles: SubtitleSegment[]): string {
  const originalText = subtitles.map((s) => s.text).join(' ');

  return `You are an IELTS speaking coach analyzing a retelling exercise.

## Original Content
${originalText}

## Task
The user will retell this content in their own words. Analyze their retelling for:
1. Key point coverage - which main ideas they covered, partially covered, or missed
2. Vocabulary suggestions - words/phrases they could upgrade
3. Overall fluency and band estimate

## Response Format (JSON only, no markdown)
{
  "keyPoints": [
    {
      "originalPoint": "the main idea from original",
      "coverage": "full" | "partial" | "missing",
      "userExpression": "what user said (if any)",
      "feedback": "brief comment"
    }
  ],
  "coverageScore": 0-100,
  "suggestedVocab": [
    {
      "word": "better word/phrase",
      "userAlternative": "what user said instead",
      "context": "original sentence",
      "suggestion": "why this is better"
    }
  ],
  "hesitations": [
    {
      "position": "where in speech",
      "transcript": "um, uh, etc"
    }
  ],
  "bandEstimate": 6.5,
  "overallFeedback": "2-3 sentence summary"
}

Listen to the audio and analyze. Return ONLY valid JSON.`;
}

/**
 * 分析 Retelling 录音
 */
export async function analyzeRetelling(
  subtitles: SubtitleSegment[],
  audioBlob: Blob,
  mimeType: string
): Promise<RetellingAnalysis> {
  const prompt = buildRetellingPrompt(subtitles);

  const response = await callGeminiAudio({
    apiKey: '',
    blob: audioBlob,
    prompt,
    mimeType,
  });

  // 解析 JSON 响应
  try {
    // 尝试直接解析
    const parsed = JSON.parse(response);
    return normalizeAnalysis(parsed);
  } catch {
    // 尝试提取 JSON 块
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        return normalizeAnalysis(parsed);
      } catch {
        throw new Error('无法解析 AI 响应中的 JSON 块');
      }
    }
    throw new Error('无法解析 AI 响应');
  }
}

/**
 * 标准化分析结果
 */
function normalizeAnalysis(raw: any): RetellingAnalysis {
  return {
    keyPoints: Array.isArray(raw.keyPoints)
      ? raw.keyPoints.map((kp: any) => ({
          originalPoint: String(kp.originalPoint || ''),
          coverage: validateCoverage(kp.coverage),
          userExpression: kp.userExpression ? String(kp.userExpression) : undefined,
          feedback: kp.feedback ? String(kp.feedback) : undefined,
        }))
      : [],
    coverageScore: typeof raw.coverageScore === 'number'
      ? Math.min(100, Math.max(0, raw.coverageScore))
      : 50,
    suggestedVocab: Array.isArray(raw.suggestedVocab)
      ? raw.suggestedVocab.map((v: any) => ({
          word: String(v.word || ''),
          userAlternative: v.userAlternative ? String(v.userAlternative) : undefined,
          context: String(v.context || ''),
          suggestion: String(v.suggestion || ''),
        }))
      : [],
    hesitations: Array.isArray(raw.hesitations)
      ? raw.hesitations.map((h: any) => ({
          position: String(h.position || ''),
          transcript: String(h.transcript || ''),
        }))
      : undefined,
    bandEstimate: typeof raw.bandEstimate === 'number'
      ? Math.min(9, Math.max(0, raw.bandEstimate))
      : undefined,
    overallFeedback: String(raw.overallFeedback || '分析完成'),
  };
}

function validateCoverage(value: any): 'full' | 'partial' | 'missing' {
  if (value === 'full' || value === 'partial' || value === 'missing') {
    return value;
  }
  return 'missing';
}

/**
 * 提取原文的关键点（用于预览）
 */
export async function extractKeyPoints(subtitles: SubtitleSegment[]): Promise<string[]> {
  const originalText = subtitles.map((s) => s.text).join(' ');

  const prompt = `Extract 3-5 key points from this text that a retelling should cover. Return as JSON array of strings.

Text: ${originalText}

Response format: ["point 1", "point 2", "point 3"]`;

  try {
    const response = await callGeminiText('', prompt);
    const parsed = JSON.parse(response);
    if (Array.isArray(parsed)) {
      return parsed.map(String);
    }
    return [];
  } catch {
    return [];
  }
}
